/**
 * Deutsche Oberflächentexte.
 *
 * Aufbau nach Bereichen. Platzhalter stehen in geschweiften Klammern und werden
 * beim Übersetzen mit Werten belegt, z.B. t('peers.limitReached', { max: 32 }).
 */
export const de = {
  common: {
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    create: 'Anlegen',
    close: 'Schließen',
    search: 'Suchen …',
    loading: 'Wird geladen …',
    none: 'keine',
    notConfigured: 'nicht konfiguriert',
    never: 'nie',
    yes: 'Ja',
    no: 'Nein',
    active: 'aktiv',
    inactive: 'inaktiv',
    total: 'gesamt',
    of: 'von',
    perMonth: '/Mo',
    included: 'inklusive',
    select: 'wählen',
    copy: 'Kopieren',
    copied: 'Kopiert',
    download: 'Herunterladen',
    back: 'Zurück',
    actions: 'Aktionen',
    status: 'Status',
    name: 'Name',
    optional: 'optional',
    required: 'Pflichtfeld',
    error: 'Fehler',
    retry: 'Erneut versuchen',
  },

  brand: {
    tagline: 'WireGuard Verwaltung',
  },

  nav: {
    section: 'Verwaltung',
    operations: 'Betrieb',
    dashboard: 'Übersicht',
    instances: 'Instanzen',
    plans: 'Tarife',
    account: 'Konto',
    admin: 'Administration',
    adminServers: 'Server',
    adminIpam: 'IP-Verwaltung',
    adminInstances: 'Instanzen',
    billing: 'Guthaben',
    invoices: 'Rechnungen',
    adminUsers: 'Benutzer',
    adminBilling: 'Guthaben',
    adminPlans: 'Tarife',
    adminMail: 'E-Mails',
    adminSettings: 'Einstellungen',
    adminAudit: 'Protokoll',
    logout: 'Abmelden',
    openMenu: 'Menü öffnen',
    closeMenu: 'Menü schließen',
    toggleSidebar: 'Seitenleiste ein- und ausklappen',
    accountSecurity: 'Konto und Sicherheit',
    quickSearch: 'Seite oder Instanz suchen …',
    quickHintNav: 'Pfeiltasten zum Navigieren',
    quickHintOpen: 'Enter zum Öffnen',
    quickHintClose: 'Esc schließt',
    noResults: 'Keine Treffer.',
    groupNavigation: 'Navigation',
    groupInstances: 'Instanzen',
    language: 'Sprache',
    themeToLight: 'Zur hellen Ansicht wechseln',
    themeToDark: 'Zur dunklen Ansicht wechseln',
  },

  live: {
    live: 'Live',
    connecting: 'Verbinde',
    offline: 'Getrennt',
    hint: 'Zustand der Live-Verbindung zum Server',
    peersOnline: '{count} Peers online',
    peerOnline: '{count} Peer online',
  },

  auth: {
    signInTitle: 'Melden Sie sich an, um Ihre Instanzen zu verwalten.',
    signUpTitle: 'Konto anlegen und den ersten Tarif buchen.',
    email: 'E-Mail-Adresse',
    password: 'Passwort',
    name: 'Name',
    nameHint: 'Optional, erscheint im Konto.',
    passwordHint: 'Mindestens 10 Zeichen.',
    signIn: 'Anmelden',
    signUp: 'Konto anlegen',
    noAccount: 'Noch kein Konto?',
    hasAccount: 'Bereits registriert?',
    toSignUp: 'Jetzt registrieren',
    toSignIn: 'Zur Anmeldung',
    totpCode: 'Zwei-Faktor-Code',
    totpHint: 'Code aus der Authenticator-App oder ein Wiederherstellungscode.',
    secured: 'Verbindung und Sitzung sind Ende-zu-Ende abgesichert.',
    signedOut: 'Sie wurden abgemeldet.',
    failed: 'Anmeldung fehlgeschlagen',

    forgotPassword: 'Passwort vergessen?',
    forgotTitle: 'Passwort zurücksetzen',
    forgotSubtitle: 'Wir schicken Ihnen einen Link an Ihre E-Mail-Adresse.',
    forgotSubmit: 'Link anfordern',
    forgotSent:
      'Falls ein Konto mit dieser Adresse existiert, ist der Link unterwegs. Bitte prüfen Sie auch den Spam-Ordner.',
    resetTitle: 'Neues Passwort vergeben',
    resetSubtitle: 'Alle bestehenden Anmeldungen werden dabei beendet.',
    resetSubmit: 'Passwort speichern',
    resetSuccess: 'Passwort gespeichert. Sie sind jetzt angemeldet.',
    resetInvalid: 'Der Link ist ungültig oder abgelaufen. Fordern Sie einen neuen an.',
    newPassword: 'Neues Passwort',
    repeatPassword: 'Passwort wiederholen',
    passwordMismatch: 'Die Passwörter stimmen nicht überein.',
    passwordTooShort: 'Mindestens 10 Zeichen.',
    backToLogin: 'Zurück zur Anmeldung',

    verifyTitle: 'E-Mail-Adresse bestätigen',
    verifyPending: 'Adresse wird bestätigt …',
    verifySuccess: 'Adresse bestätigt. Willkommen!',
    verifyInvalid: 'Der Bestätigungslink ist ungültig oder abgelaufen.',
    verifyBannerTitle: 'E-Mail-Adresse noch nicht bestätigt',
    verifyBannerText: 'Bestätigen Sie Ihre Adresse, um Instanzen bestellen zu können.',
    verifyResend: 'Link erneut senden',
    verifyResent: 'Bestätigungslink verschickt.',
    verifyAlready: 'Ihre Adresse ist bereits bestätigt.',
    registerVerifySent:
      'Konto angelegt! Wir haben Ihnen einen Bestätigungslink geschickt – bitte prüfen Sie Ihr Postfach (auch den Spam-Ordner). Nach dem Klick sind Sie automatisch angemeldet.',
  },

  dashboard: {
    title: 'Übersicht',
    subtitle: 'Alle gebuchten WireGuard-Instanzen und ihre aktuelle Auslastung.',
    orderPlan: 'Tarif buchen',
    instances: 'Instanzen',
    activeCount: '{count} aktiv',
    peersOnline: 'Peers online',
    inbound: 'Eingehend (Client-Upload)',
    outbound: 'Ausgehend (Client-Download)',
    emptyTitle: 'Noch keine Instanz gebucht',
    emptyText:
      'Wählen Sie einen Tarif – die WireGuard-Instanz wird binnen Sekunden eingerichtet und ist sofort einsatzbereit.',
    emptyAction: 'Tarife ansehen',
    uplinkLoad: 'Auslastung des Uplinks',
    peerSlots: 'Belegte Peer-Plätze',
    syncedAgo: 'Abgleich {time}',
  },

  instances: {
    title: 'Instanzen',
    subtitle: 'Jede Instanz entspricht einem eigenen WireGuard-Interface auf dem Server.',
    searchPlaceholder: 'Nach Name, Interface oder Netz suchen',
    searchLabel: 'Instanzen durchsuchen',
    columnInstance: 'Instanz',
    columnPlan: 'Tarif',
    columnPeers: 'Peers',
    columnRate: 'Datenrate',
    columnLoad: 'Auslastung',
    columnSync: 'Abgleich',
    noMatch: 'Keine Instanz passt zur Suche.',
    emptyTitle: 'Noch keine Instanz gebucht',
    emptyText: 'Buchen Sie einen Tarif, um Ihre erste WireGuard-Instanz einzurichten.',
    backToList: 'Alle Instanzen',
    notFound: 'Instanz nicht gefunden',
    notFoundText: 'Die Instanz existiert nicht oder gehört zu einem anderen Konto.',
    dedicated: 'Dedizierte IPv4',
    shared: 'Geteiltes NAT',
    inbound: 'Eingehend',
    outbound: 'Ausgehend',
    uplink: 'Uplink',
    peers: 'Peers',
    delete: 'Instanz löschen',
    deleteConfirm:
      'Die Instanz „{name}" wird mit allen {count} Peers abgeschaltet. Alle Verbindungen enden sofort und es fallen keine weiteren Kosten an. Versehentlich gelöscht? Der Betreiber kann die Instanz innerhalb von 72 Stunden vollständig wiederherstellen – danach ist sie endgültig weg.',
    deleteFinal: 'Instanz löschen',
    deleted: 'Instanz wurde gelöscht.',
    tabPeers: 'Peers',
    tabTraffic: 'Datenverkehr',
    tabSettings: 'Einstellungen',
    trafficTitle: 'Verlauf der letzten 24 Stunden',
    trafficSubtitle: 'Grün: Upload der Clients, Blau: Download der Clients',
    utilization: 'Auslastung',
    utilizationOfLimit: '{percent}% vom Tariflimit',
    totalReceived: 'Gesamt empfangen',
    totalSent: 'Gesamt gesendet',
    planLimit: 'Tariflimit',
    settingsGeneral: 'Allgemein',
    settingsGeneralHint: 'Name, DNS und MTU der Instanz',
    label: 'Bezeichnung',
    dnsServers: 'DNS-Server der Clients',
    mtu: 'MTU',
    mtuHint: '1420 passt für die meisten Anschlüsse. Niedrigere Werte helfen bei PPPoE-Leitungen.',
    siteToSiteNets: 'Freigegebene Site-to-Site-Netze',
    siteToSiteHint: 'Nur diese privaten Netze dürfen über den Tunnel erreicht werden.',
    settingsSaved: 'Einstellungen gespeichert.',
    uplinkHint: 'Änderungen greifen sofort, bestehende Verbindungen bleiben bestehen.',
    uplinkChanged: 'Uplink auf {value} geändert.',
    uplinkConfirmTitle: 'Uplink ändern?',
    uplinkConfirmText:
      'Der Uplink wird sofort auf {bandwidth} umgestellt – bestehende Verbindungen bleiben dabei erhalten.',
    uplinkConfirmAction: 'Kostenpflichtig ändern',
    uplinkPriceCurrent: 'Bisheriger Monatspreis',
    uplinkPriceNew: 'Neuer Monatspreis',
    uplinkPriceHint: 'Der neue Preis gilt ab der nächsten Abbuchung. Es gibt keine anteilige Verrechnung.',
    includedInPlan: 'im Tarif enthalten',
    surcharge: '+{price} pro Monat',
    connectionData: 'Verbindungsdaten',
    endpoint: 'Endpoint',
    serverPublicKey: 'Server Public Key',
    ipv4Overlay: 'Internes IPv4-Netz',
    ipv6Prefix: 'IPv6-Präfix',
    outgoingIpv4: 'Ausgehende IPv4',
    dedicatedSuffix: '(dediziert)',
    sharedNat: 'geteilt (NAT)',
    plan: 'Tarif',
    lastSync: 'Letzter Abgleich',
    notAssigned: 'nicht zugewiesen',
  },

  peers: {
    title: 'Peers',
    slotsUsed: '{used} von {max} Plätzen belegt',
    add: 'Peer anlegen',
    emptyTitle: 'Noch keine Peers',
    emptyText:
      'Legen Sie den ersten Peer an – Schlüssel, IP-Adresse und QR-Code werden automatisch erzeugt.',
    columnAddresses: 'Adressen',
    columnHandshake: 'Letzter Handshake',
    columnTraffic: 'Verkehr',
    inSuffix: 'ein',
    outSuffix: 'aus',
    online: 'Online',
    offline: 'Offline',
    disabled: 'Deaktiviert',
    deviceName: 'Name des Geräts',
    devicePlaceholder: 'Laptop Anna',
    keys: 'Schlüssel',
    keysServer: 'Serverseitig erzeugen',
    keysServerHint: 'Konfiguration und QR-Code werden einmalig angezeigt',
    keysOwn: 'Eigenen Public Key',
    keysOwnHint: 'Privater Schlüssel bleibt auf dem Gerät',
    publicKey: 'Public Key',
    publicKeyPlaceholder: 'base64-kodierter X25519-Schlüssel',
    clientAllowedIps: 'AllowedIPs des Clients',
    clientAllowedIpsHint:
      '0.0.0.0/0 leitet den gesamten Verkehr durch den Tunnel. Für Split-Tunnel nur einzelne Netze angeben.',
    routing: 'Weiterleitung',
    routingFull: 'Gesamter Verkehr',
    routingFullHint: 'Alles läuft durch den Tunnel',
    routingTunnel: 'Nur Tunnelnetz',
    routingTunnelHint: 'Split-Tunnel: nur die Netze dieser Instanz',
    routingCustom: 'Eigene Angabe',
    routingCustomHint: 'Netze selbst festlegen',

    reopenConfig: 'Konfiguration ansehen',
    configWithoutKey: 'Ohne privaten Schlüssel',
    configWithoutKeyText:
      'Der private Schlüssel wird nie gespeichert und fehlt deshalb in dieser Fassung. Tragen Sie ihn ein, wenn Sie ihn noch haben – andernfalls erzeugen Sie über „Neuen Schlüssel erzeugen" ein frisches Paar.',
    behindPeer: 'Netze hinter diesem Peer',
    behindPeerHint: 'Site-to-Site, kommagetrennt',
    usePsk: 'Pre-Shared Key verwenden (zusätzlicher Schutz gegen Quantenangriffe)',
    created: 'Peer „{name}" wurde angelegt.',
    deleteConfirm:
      'Soll „{name}" dauerhaft entfernt werden? Das Gerät verliert sofort den Zugang; alle anderen Verbindungen bleiben bestehen.',
    removed: 'Peer „{name}" wurde entfernt.',
    enabled: '{name} aktiviert.',
    disabledToast: '{name} deaktiviert.',
    limitReached: 'Das Peer-Limit des Tarifs ist erreicht ({max} Peers).',
    showConfig: 'Konfiguration und QR-Code',
    rotate: 'Neuen Schlüssel erzeugen',
    rotateTitle: 'Neuen Schlüssel erzeugen?',
    rotateText:
      'Für „{name}" wird ein neues Schlüsselpaar erzeugt. Die bisherige Konfiguration verliert sofort ihre Gültigkeit und muss auf dem Gerät ersetzt werden.',
    rotateConfirm: 'Schlüssel erneuern',
    rotated: 'Neuer Schlüssel erzeugt – bitte Konfiguration übertragen.',
    toggleEnable: 'Aktivieren',
    toggleDisable: 'Deaktivieren',
  },

  config: {
    title: 'Konfiguration: {name}',
    onceOnlyTitle: 'Diese Konfiguration wird nur einmal angezeigt',
    onceOnlyText:
      'Der private Schlüssel wird ausschließlich hier ausgeliefert und nirgends gespeichert. Sichern Sie die Datei jetzt – später lässt sich nur ein neuer Schlüssel erzeugen.',
    qrTab: 'QR-Code',
    fileTab: 'Konfigurationsdatei',
    scanHint: 'In der WireGuard-App „Aus QR-Code hinzufügen" wählen und diesen Code scannen.',
    copyText: 'Text kopieren',
    downloadConf: '.conf herunterladen',
    copyFailed: 'Kopieren nicht möglich. Bitte den Text manuell markieren.',
    noStoredKey: 'Für diesen Peer liegt keine Konfiguration vor.',
    noStoredKeyText:
      'Der private Schlüssel wurde nie gespeichert. Erzeugen Sie einen neuen Schlüssel, um eine frische Konfiguration zu erhalten.',
  },

  plans: {
    title: 'Tarife & Preise',
    subtitle: 'Monatlich kündbar, jederzeit upgradebar und mit DDoS-Schutz ab der ersten Minute.',
    popular: 'Beliebt',
    uplink: 'Uplink',
    clientsPerServer: '{count} Clients pro Server',
    dedicatedIpv4Included: '1 dedizierte IPv4 inklusive',
    sharedIpv4: 'Geteilte IPv4 (eigene ab +2,50 €)',
    ipv6Included: 'Eigenes IPv6 /64 inklusive',
    ddos: 'DDoS-Schutz inklusive',
    unlimited: 'Unlimitierter Traffic (Fair-Use)',
    siteToSite: 'Site-to-Site Routing inklusive',
    splitTunnel: 'Split-Tunnel & Kill-Switch',
    prioritySupport: 'Priorisierter Netzwerk-Support',
    networkUpgrade: 'Netzwerk-Upgrade',
    from: 'ab',
    order: 'Bestellen',
    footnote:
      'Der Uplink lässt sich in jedem Tarif hochstufen – der Aufpreis gilt monatlich und ist jederzeit wieder änderbar.',
    orderTitle: '{plan} bestellen',
    orderSubtitle: 'Die Instanz wird sofort provisioniert und ist anschließend einsatzbereit.',
    instanceName: 'Bezeichnung',
    instanceNameHint: 'Frei wählbar, z.B. der Standort oder das Team.',
    addons: 'Zubuchungen',
    dedicatedIpv4Addon: 'Dedizierte IPv4-Adresse',
    dedicatedIpv4AddonHint: 'Exklusive öffentliche Adresse statt geteiltem NAT',
    basePrice: 'Grundpreis',
    monthly: 'Monatlich',
    orderConfirm: 'Kostenpflichtig bestellen',
    ordered: '{name} wurde eingerichtet ({ifName}).',
    orderFailed: 'Bestellung fehlgeschlagen',
  },

  account: {
    title: 'Konto',
    subtitle: 'Zugangsdaten und Zwei-Faktor-Authentifizierung.',
    profile: 'Profil',
    role: 'Rolle',
    roleAdmin: 'Administrator',
    roleCustomer: 'Kunde',
    memberSince: 'Konto seit',
    changePassword: 'Passwort ändern',
    changePasswordHint: 'Alle anderen Sitzungen werden danach abgemeldet.',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    passwordChanged: 'Passwort geändert. Andere Sitzungen wurden abgemeldet.',
    twoFactor: 'Zwei-Faktor-Authentifizierung',
    twoFactorHint: 'Zusätzlicher Schutz mit einer Authenticator-App (TOTP).',
    twoFactorActive: 'Aktiv',
    twoFactorInactive: 'Nicht eingerichtet',
    twoFactorActiveHint: 'Bei jeder Anmeldung wird ein Code abgefragt.',
    twoFactorInactiveHint: 'Empfohlen für Konten mit produktiven Instanzen.',
    setUp: 'Einrichten',
    disable: 'Deaktivieren',
    setupTitle: 'Zwei-Faktor-Authentifizierung einrichten',
    setupIntro:
      'Sie benötigen eine Authenticator-App wie Aegis, 1Password oder Google Authenticator. Im nächsten Schritt erhalten Sie ein Geheimnis, das Sie dort hinterlegen.',
    next: 'Weiter',
    secret: 'Geheimnis',
    secretCopied: 'Geheimnis kopiert.',
    codeFromApp: 'Code aus der App',
    activate: 'Aktivieren',
    recoveryIntro:
      'Bewahren Sie diese Wiederherstellungscodes sicher auf. Jeder Code funktioniert genau einmal und ersetzt den Code aus der App.',
    done: 'Fertig',
    disableTitle: 'Zwei-Faktor-Authentifizierung deaktivieren',
    disableText:
      'Bestätigen Sie mit Ihrem Passwort. Danach genügt zur Anmeldung wieder das Passwort allein.',
    disabled: 'Zwei-Faktor-Authentifizierung deaktiviert.',
  },

  admin: {
    title: 'Administration',
    subtitle: 'Server, IP-Verwaltung, Kundeninstanzen und Protokolle.',
    reconcile: 'Systemzustand abgleichen',
    reconciled: 'Abgleich abgeschlossen: {restored} wiederhergestellt, {failed} fehlgeschlagen.',
    servers: 'Server',
    customers: 'Kunden',
    dedicatedIpv4: 'Dedizierte IPv4',
    freeCount: '{count} frei',
    inPool: '{count} im Pool',
    publicIp: 'Öffentliche IPv4',
    wanInterface: 'Uplink-Interface',
    internalNet: 'Internes Netz',
    portRange: 'Portbereich',
    ifIndexRange: 'Interface-Indizes',
    capacity: 'Kapazität',
    noServer: 'Noch kein Server eingerichtet. Der Installer legt ihn beim Aufsetzen automatisch an.',
    ipv4Pools: 'IPv4-Pools',
    ipv6Pools: 'IPv6-Präfixe',
    strategyDescending: 'absteigend ab höchster Adresse',
    strategyAscending: 'aufsteigend',
    ipamTitle: 'IP-Verwaltung',
    ipamSubtitle:
      'Öffentliche IPv4-Adressen werden absteigend vergeben – die höchste freie Adresse zuerst.',
    ipamAvailable: 'Frei',
    ipamAssigned: 'Belegt',
    ipamReserved: 'Reserviert',
    ipamNextAssignment: 'Nächste Vergabe',
    ipamGateway: 'Gateway',
    ipamNoPools: 'Für diesen Server ist noch kein öffentlicher IPv4-Pool hinterlegt.',
    ipamReserve: 'Reservieren',
    ipamRelease: 'Freigeben',
    ipamReserved_toast: 'Adresse reserviert.',
    ipamReleased: 'Adresse freigegeben.',
    ipamAssignedTo: 'Zugewiesen an {instance}',
    ipv6Allocations: 'Vergebene Präfixe',
    ipv6NoAllocations: 'Noch keine Präfixe vergeben.',
    customer: 'Kunde',
    net: 'Netz',
    suspend: 'Aussetzen',
    resume: 'Fortsetzen',
    suspended: '{name} wurde ausgesetzt.',
    resumed: '{name} wurde fortgesetzt.',
    editUser: 'Bearbeiten',
    editUserTitle: 'Konto bearbeiten – {email}',
    userSaved: 'Konto {email} gespeichert.',
    roleSelfHint: 'Die eigene Rolle lässt sich nicht ändern.',
    emailVerifiedLabel: 'E-Mail-Adresse gilt als bestätigt',
    newPassword: 'Neues Passwort setzen',
    newPasswordHint: 'Leer lassen, um es nicht zu ändern. Beendet alle Sitzungen des Kontos.',
    sendVerificationTitle: 'Bestätigungslink',
    sendVerificationHint: 'Falls die Mail nicht ankam. Erzeugt einen frischen Link, ältere verfallen.',
    sendVerification: 'Link senden',
    sendVerificationDone: 'Bestätigungslink an {to} verschickt.',
    totpResetTitle: 'Zwei-Faktor-Anmeldung',
    totpResetHint: 'Für Kunden, die ihr Gerät verloren haben. Beendet alle Sitzungen.',
    totpReset: '2FA zurücksetzen',
    totpResetDone: 'Zwei-Faktor-Anmeldung von {email} zurückgesetzt.',
    deleteUserTitle: 'Konto löschen',
    deleteUserHint: 'Unwiderruflich – Guthaben, Buchungen und Rechnungen werden mit entfernt. Zur Bestätigung {email} eintippen.',
    deleteUserBlocked: 'Nicht möglich: das Konto hat noch {count} Instanz(en). Erst löschen bzw. den Papierkorb leeren.',
    userDeleted: 'Konto {email} wurde gelöscht.',
    restore: 'Wiederherstellen',
    restored: '{name} wurde wiederhergestellt – der Kunde wurde benachrichtigt.',
    purge: 'Endgültig löschen',
    purgeTitle: 'Endgültig löschen?',
    purgeText:
      '„{name}" wird sofort und unwiderruflich entfernt. Peers, Schlüssel und die reservierten IP-Adressen werden freigegeben – eine Wiederherstellung ist danach nicht mehr möglich.',
    purged: '{name} wurde endgültig gelöscht.',
    trashUntil: 'endgültig weg am {date}',
    users: 'Benutzer',
    createUser: 'Benutzer anlegen',
    userCreated: 'Benutzer wurde angelegt.',
    initialPassword: 'Initialpasswort',
    twoFa: '2FA',
    lastLogin: 'Letzte Anmeldung',
    state: 'Zustand',
    locked: 'gesperrt',
    activate: 'Aktivieren',
    deactivate: 'Deaktivieren',
    userToggled: '{email} wurde {state}.',
    activated: 'aktiviert',
    deactivated: 'deaktiviert',
    auditTitle: 'Protokoll',
    auditSubtitle: 'Die letzten sicherheitsrelevanten Aktionen',
    auditFilter: 'Nach Aktion, Konto oder IP filtern',
    auditEmpty: 'Noch keine Einträge',
    auditEmptyText: 'Sobald Aktionen ausgeführt werden, erscheinen sie hier.',
    auditNoMatch: 'Keine Treffer',
    auditNoMatchText: 'Passen Sie den Filter an.',
    anonymous: 'anonym',
  },

  billing: {
    title: 'Guthaben',
    subtitle: 'Kontostand, Aufladungen und Buchungen.',
    balance: 'Guthaben',
    available: 'Verfügbar',
    creditLimit: 'Verfügungsrahmen',
    monthlyTotal: 'Monatlich fällig',
    monthlyHint: 'Summe aller laufenden Instanzen.',
    availableHint: 'Guthaben zuzüglich Verfügungsrahmen.',
    noCreditLimit: 'Kein Verfügungsrahmen eingeräumt.',
    coversMonths: 'Reicht für etwa {months} Monate.',
    coversLessThanMonth: 'Reicht nicht für den nächsten Monat.',

    topUp: 'Guthaben aufladen',
    topUpWithPayPal: 'Mit PayPal aufladen',
    topUpAmount: 'Betrag',
    topUpRange: 'Zwischen {min} und {max}.',
    topUpUnavailable: 'Die Aufladung ist auf diesem Server nicht eingerichtet.',
    topUpUnavailableHint: 'Wenden Sie sich an den Betreiber, um Guthaben aufzuladen.',
    topUpRedirect: 'Sie werden zu PayPal weitergeleitet …',
    topUpSuccess: '{amount} wurden gutgeschrieben.',
    topUpCancelled: 'Die Aufladung wurde abgebrochen.',
    topUpAlreadyCredited: 'Diese Zahlung wurde bereits gutgeschrieben.',

    lowBalanceWarning: 'Ihr Guthaben reicht nicht mehr für die nächste Abbuchung.',
    negativeBalance: 'Ihr Konto ist im Minus. Bitte laden Sie auf.',
    suspendedWarning:
      'Mindestens eine Instanz ist wegen fehlender Deckung gesperrt. Nach Ablauf der Frist wird sie gelöscht.',

    history: 'Buchungen',
    historyEmpty: 'Noch keine Buchungen',
    historyEmptyText: 'Aufladungen und Abbuchungen erscheinen hier.',
    date: 'Datum',
    description: 'Vorgang',
    amount: 'Betrag',
    balanceAfter: 'Kontostand',

    typeTOPUP: 'Aufladung',
    typeCHARGE: 'Abbuchung',
    typeREFUND: 'Erstattung',
    typeADJUSTMENT: 'Korrektur',
    sourceMANUAL: 'manuell',
    sourcePAYPAL: 'PayPal',
    sourceSYSTEM: 'automatisch',

    invoicesTitle: 'Rechnungen',
    invoicesSubtitle: 'Ausgestellte Rechnungen des Kontos.',
    invoicesEmpty: 'Keine Rechnungen',
    invoicesEmptyText: 'Sobald eine Abrechnung erstellt wurde, erscheint sie hier.',
    invoiceNumber: 'Nummer',
    invoicePeriod: 'Zeitraum',
    invoiceDue: 'Fällig',
    invoiceNet: 'Netto',
    invoiceTax: 'Umsatzsteuer ({rate} %)',
    invoiceGross: 'Gesamt',
    invoiceOpen: 'Offen',
    invoicePaid: 'Bezahlt',
    invoiceCancelled: 'Storniert',
    invoiceDraft: 'Entwurf',
    invoiceItems: 'Positionen',
    invoiceRecipient: 'Rechnungsempfänger',
    backToInvoices: 'Zurück zur Übersicht',

    addressTitle: 'Rechnungsanschrift',
    addressSubtitle: 'Wird auf Rechnungen übernommen.',
    company: 'Firma',
    street: 'Straße und Hausnummer',
    zip: 'Postleitzahl',
    city: 'Ort',
    country: 'Land',
    vatId: 'Umsatzsteuer-ID',
    addressSaved: 'Anschrift gespeichert.',

    modePREPAID: 'Guthaben',
    modeINVOICE: 'Rechnung',
    modeLabel: 'Abrechnung',
    invoiceModeHint: 'Ihre Instanzen werden monatlich in Rechnung gestellt statt vom Guthaben abgebucht.',

    adminTitle: 'Guthaben und Abrechnung',
    adminSubtitle: 'Konten aufladen, Verfügungsrahmen setzen und Rechnungen verwalten.',
    selectUser: 'Konto wählen',
    adjustTitle: 'Buchung anlegen',
    adjustAmount: 'Betrag (negativ belastet das Konto)',
    adjustNote: 'Grund',
    adjustNoteHint: 'Wird in der Buchung festgehalten und ist für den Kunden sichtbar.',
    adjustBooked: 'Buchung angelegt.',
    limitsTitle: 'Grenzen',
    limitsSaved: 'Grenzen gespeichert.',
    lowBalanceThreshold: 'Warnschwelle',
    lowBalanceHint: 'Ab diesem verfügbaren Betrag wird eine Warnmail verschickt. -1 schaltet sie ab.',
    markPaid: 'Als bezahlt markieren',
    issueInvoice: 'Jetzt ausstellen',
    invoiceIssued: 'Rechnung {number} ausgestellt.',
    invoiceMarkedPaid: 'Rechnung als bezahlt markiert.',
    allUsers: 'Alle Konten',
  },

  plansAdmin: {
    title: 'Tarife',
    frozenHint: 'Preisänderungen gelten nur für neue Bestellungen – laufende Instanzen behalten ihren Preis.',
    create: 'Tarif anlegen',
    editTitle: 'Tarif bearbeiten – {name}',
    plan: 'Tarif',
    price: 'Preis',
    priceField: 'Monatspreis (€)',
    limits: 'Limits',
    peersCount: '{count} Peers',
    features: 'Merkmale',
    tierCount: '{count} Uplink-Stufen',
    name: 'Name',
    slugHint: 'Eindeutig, nur Kleinbuchstaben, Ziffern und Bindestriche. Nach dem Anlegen nicht mehr ändern.',
    description: 'Beschreibung',
    baseBandwidth: 'Uplink (Mbit/s)',
    sortOrder: 'Sortierung',
    ipMode: 'Ausgehende IPv4',
    isPublic: 'Im Tarifkatalog sichtbar',
    tiers: 'Buchbare Uplink-Stufen',
    tiersHint: 'Aufpreise auf den Grundpreis. Kunden können jederzeit zwischen den Stufen wechseln.',
    addTier: 'Stufe hinzufügen',
    hiddenBadge: 'versteckt',
    hide: 'Aus dem Katalog nehmen',
    show: 'Im Katalog anzeigen',
    hidden: '„{name}" ist jetzt versteckt – Bestandskunden laufen unverändert weiter.',
    shown: '„{name}" ist jetzt im Katalog sichtbar.',
    saved: 'Tarif gespeichert.',
    deleted: 'Tarif „{name}" wurde gelöscht.',
    deleteTitle: 'Tarif löschen?',
    deleteText: '„{name}" wird entfernt. Das geht nur, solange keine Instanz den Tarif nutzt – sonst lieber verstecken.',
  },

  settingsAdmin: {
    title: 'Einstellungen',
    subtitle: 'Panel-Werte überschreiben die .env. Zurücksetzen stellt den Serverwert wieder her.',
    saved: 'Einstellungen gespeichert – sie wirken sofort, ohne Neustart.',
    saveChanges: '{count} Änderung(en) speichern',
    overridden: 'Panel',
    willReset: 'wird zurückgesetzt',
    reset: 'Auf .env-Wert zurücksetzen',
    envIs: 'Aus .env: {value}',
    envEmpty: 'In der .env nicht gesetzt.',
    envSet: 'In der .env gesetzt.',
    secretPlaceholder: 'Neues Geheimnis eingeben',
    testMail: 'Testmail senden',
    mailOk: 'Testmail an {to} verschickt – bitte Posteingang prüfen.',
    mailFailed: 'Versand fehlgeschlagen – Details im Dienstprotokoll.',
    testPaypal: 'Zugang prüfen',
    paypalOk: 'PayPal-Zugang gültig ({mode}).',
    paypalFailed: 'PayPal-Prüfung fehlgeschlagen: {message}',

    group_smtp: 'E-Mail-Versand',
    group_smtp_hint: 'SMTP-Zugang und Absender. Port 587 = STARTTLS (secure aus), 465 = TLS (secure an).',
    group_paypal: 'PayPal',
    group_paypal_hint: 'Zugangsdaten aus dem Developer-Portal. Ohne Client-ID und Secret ist die Aufladung aus.',
    group_whmcs: 'WHMCS-Anbindung',
    group_whmcs_hint:
      'Bruecke zu WHMCS: Bestellung, Zahlung und Kuendigung steuert WHMCS, FluxPanel provisioniert. Das Token hier muss mit dem im WHMCS-Modul uebereinstimmen.',
    group_registration: 'Registrierung',
    group_registration_hint: 'Wer darf sich anmelden, und ab wann darf bestellt werden.',
    group_billing: 'Abrechnung und Fristen',
    group_billing_hint: 'Beträge in Cent, Steuersatz in Promille (190 = 19 %).',

    k_smtp_host: 'SMTP-Host',
    k_smtp_port: 'Port',
    k_smtp_secure: 'Implizites TLS (nur Port 465)',
    k_smtp_user: 'Benutzername',
    k_smtp_password: 'Passwort',
    k_smtp_allowInsecureTls: 'Selbstsignierte Zertifikate zulassen',
    k_mail_from: 'Absenderadresse',
    k_mail_fromName: 'Absendername',
    k_mail_replyTo: 'Antwortadresse (optional)',
    k_mail_bcc: 'Blindkopie an den Betreiber (optional)',
    k_mail_publicUrl: 'Linkbasis der Mails (Panel-URL)',

    k_paypal_mode: 'Umgebung',
    k_paypal_clientId: 'Client-ID',
    k_paypal_clientSecret: 'Client-Secret',
    k_paypal_webhookId: 'Webhook-ID',
    k_paypal_minTopupCents: 'Mindestaufladung (Cent)',
    k_paypal_maxTopupCents: 'Höchstaufladung (Cent)',

    k_whmcs_apiToken: 'API-Token (gemeinsames Geheimnis)',
    k_whmcs_suppressMails: 'Keine System-Mails fuer WHMCS-Instanzen (WHMCS mailt selbst)',
    k_registration_open: 'Selbstregistrierung erlaubt',
    k_registration_requireVerifiedEmail: 'Bestellen erst nach E-Mail-Bestätigung',

    k_billing_taxRatePermille: 'Steuersatz (Promille)',
    k_billing_defaultCreditLimitCents: 'Standard-Verfügungsrahmen neuer Konten (Cent)',
    k_billing_lowBalanceCents: 'Warnschwelle Guthaben (Cent, -1 = aus)',
    k_billing_suspendGraceDays: 'Frist Sperrung bis Löschung (Tage)',
    k_billing_deleteRetentionHours: 'Papierkorbfrist (Stunden)',
    k_billing_invoiceDueDays: 'Zahlungsziel Rechnungen (Tage)',
  },

  mailAdmin: {
    title: 'E-Mail-Vorlagen',
    subtitle: 'Betreff und Text jeder Systemmail anpassen – je Sprache, mit Platzhaltern.',
    template: 'Vorlage',
    subject: 'Betreff',
    body: 'Text',
    bodyHint: 'Absätze durch Leerzeilen trennen. Platzhalter in geschweiften Klammern werden beim Versand ersetzt.',
    placeholders: 'Verfügbare Platzhalter',
    editHint: 'Leere oder unveränderte Felder nutzen den Standardtext.',
    fixedHint:
      'Anrede, Info-Kästen, Schaltflächen und Links sind fest und bleiben immer funktionsfähig – überschrieben wird nur der Fließtext.',
    overridden: 'angepasst',
    saved: 'Vorlage gespeichert.',
    reset: 'Auf Standard zurücksetzen',
    resetDone: 'Vorlage auf den Standardtext zurückgesetzt.',
    sendTest: 'Testmail an mich',
    testSent: 'Testmail an {to} verschickt.',
    smtpMissing:
      'Der Mailversand ist nicht eingerichtet (SMTP_HOST fehlt). Vorlagen lassen sich bearbeiten, aber nicht testen.',

    kind_passwordReset: 'Passwort zurücksetzen',
    kind_passwordChanged: 'Passwort geändert',
    kind_emailVerify: 'Adresse bestätigen',
    kind_lowBalance: 'Guthaben wird knapp',
    kind_topupReceived: 'Guthaben aufgeladen',
    kind_charged: 'Monatliche Abbuchung',
    kind_instanceCreated: 'Instanz bereit',
    kind_instanceSuspended: 'Instanz pausiert',
    kind_instanceResumed: 'Instanz wieder aktiv',
    kind_instanceDeleted: 'Instanz gelöscht (Kunde)',
    kind_instanceExpired: 'Instanz endgültig entfernt',
    kind_peerDeleted: 'Gerät entfernt',
    kind_invoiceIssued: 'Rechnung ausgestellt',

    logTitle: 'Versandprotokoll',
    logHint: 'Jeder Versuch mit Status – ohne Inhalte, Links gehören nicht in die Datenbank.',
    logAll: 'Alle',
    logSent: 'Verschickt',
    logFailed: 'Fehlgeschlagen',
    logDisabled: 'Versand aus',
    logEmpty: 'Noch keine Mails verschickt',
  },

  errors: {
    generic: 'Aktion fehlgeschlagen',
    loadFailed: 'Daten konnten nicht geladen werden',
    saveFailed: 'Speichern fehlgeschlagen',
    deleteFailed: 'Löschen fehlgeschlagen',
    insufficientFunds: 'Das Guthaben reicht für diese Bestellung nicht aus.',
    insufficientFundsHint: 'Es fehlen {missing}. Laden Sie Ihr Guthaben auf und bestellen Sie erneut.',
    emailNotVerified: 'Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.',
  },
} as const;

/** Weitet die Literaltypen auf `string` auf, damit Uebersetzungen abweichen duerfen. */
type DeepString<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepString<T[K]>;
};

export type Dictionary = DeepString<typeof de>;
